import "../../utils/pdfPolyfill";
import { ensurePdfjsWorker } from "../../utils/pdfWorker";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { FilePdfOutlined } from "@ant-design/icons";
import { Alert, Empty, Spin } from "antd";
import { useAndStore } from "../../base/store/useAndStore";
import { getDataSetStoreKey } from "../../base/storeKeyUtils";
import { getCoreConfig } from "../../config";
import type { BaseComponentProps, DataSetConfig } from "../../types/common";
import type { PdfViewerCommand } from "./pdfViewerCommand";

const MIN_ZOOM_SCALE = 0.5;
const MAX_ZOOM_SCALE = 3;
const ZOOM_STEP = 0.1;

export interface AndPdfViewerProps
  extends BaseComponentProps,
    DataSetConfig {
  /** PDF 路径 */
  filePath?: string;
  initialPage?: number;
  /**
   * 在 dataSet 键下写回状态的嵌套前缀，如 docPreview
   * -> docPreview.currentPage / docPreview.totalPages / docPreview.targetPage
   */
  metaPathPrefix?: string;
  /** PDF 加载失败时展示的自定义提示文案 */
  loadErrorMessage?: string;
  /** 非 PDF 页面区域背景色 */
  nonPdfBackgroundColor?: string;
  styles?: string | Record<string, unknown>;
  token?: Record<string, unknown>;
}

export const AndPdfViewer: React.FC<AndPdfViewerProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  filePath: propFilePath,
  initialPage: propInitialPage = 1,
  metaPathPrefix = "",
  loadErrorMessage,
  nonPdfBackgroundColor = "#ECECEC",
  styles: _styles,
  token: _token,
  className,
  style,
}) => {
  const setDataSet = useAndStore((s) => s.setDataSet);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet],
  );

  const storeFileValue = useAndStore((s) => {
    if (!dataSetKey || !dataSetField) return undefined;
    const v = s.getDataSet(dataSetKey, dataSetField);
    if (v !== undefined && v !== null && v !== "") return String(v);
    return undefined;
  });

  const previewPending = useAndStore((s) => {
    if (!dataSetKey) return false;
    return Boolean(s.getDataSet(dataSetKey, "previewPending"));
  });

  const storeProps = useAndStore((s) =>
    sessionId && id ? s.propsStore[`${sessionId}_props`]?.[id] ?? null : null,
  );

  const resolvedFilePath = useMemo(() => {
    let raw: string | undefined;
    if (storeFileValue !== undefined) raw = storeFileValue;
    else if (storeProps && typeof storeProps === "object") {
      const fp = (storeProps as any).filePath;
      if (fp != null && fp !== "") raw = String(fp);
    }
    if (raw === undefined) raw = propFilePath;
    return raw;
  }, [storeFileValue, storeProps, propFilePath]);

  const resolvedNonPdfBackgroundColor = useMemo(() => {
    if (storeProps && typeof storeProps === "object") {
      const color = (storeProps as any).nonPdfBackgroundColor;
      if (typeof color === "string" && color.trim()) {
        return color.trim();
      }
    }
    return nonPdfBackgroundColor;
  }, [storeProps, nonPdfBackgroundColor]);

  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(() =>
    Math.max(1, propInitialPage || 1),
  );
  const pageNumberRef = useRef(pageNumber);
  pageNumberRef.current = pageNumber;
  const [loadError, setLoadError] = useState<Error | null>(null);
  /** 换文件 / 初次加载：直到目标页 canvas 绘完才关闭 */
  const [docLoading, setDocLoading] = useState(!!resolvedFilePath);
  /** 单页模式换页：轻量遮罩，直到新页 onRenderSuccess */
  const [pagePaintPending, setPagePaintPending] = useState(false);
  const pendingPaintPageRef = useRef<number | null>(null);
  /** 与 resolvedFilePath 解耦：先卸 Document 再等 worker 释放，避免 PDFWorker.create 竞态 */
  const [displayFile, setDisplayFile] = useState<string | undefined>(undefined);
  const loadGenRef = useRef(0);
  const mountSettledRef = useRef(false);
  const loading = docLoading || pagePaintPending;

  useEffect(() => {
    ensurePdfjsWorker();
    mountSettledRef.current = false;
    return () => {
      mountSettledRef.current = false;
    };
  }, []);
  const [previewMode, setPreviewMode] = useState<"all" | "single">("all");
  const [fitMode, setFitMode] = useState<"width" | "height">("width");
  const [zoomScale, setZoomScale] = useState(1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const viewerBodyRef = useRef<HTMLDivElement>(null);
  const pageNodeMapRef = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollSyncSuppressedRef = useRef(false);
  const pendingScrollPageRef = useRef<number | null>(null);
  const wheelAccumRef = useRef(0);
  const wheelFlipLockRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(600);
  const [containerHeight, setContainerHeight] = useState(800);
  const lastCmdNonce = useRef<number>(0);
  const docOptions = useMemo(() => {
    const raw = getCoreConfig().pdfCMapUrl?.trim();
    if (!raw) return {};
    const cMapUrl = raw.endsWith("/") ? raw : `${raw}/`;
    return { cMapUrl, cMapPacked: true as const };
  }, []);

  const metaPrefix = (metaPathPrefix || "").trim();

  const targetPageFromStore = useAndStore((s) => {
    if (!dataSetKey || !metaPrefix) return undefined;
    return s.getDataSet(dataSetKey, `${metaPrefix}.targetPage`) as
      | number
      | undefined;
  });

  useEffect(() => {
    const el = viewerBodyRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0) setContainerWidth(w);
      if (h > 0) setContainerHeight(h);
    });
    ro.observe(el);
    const w0 = el.clientWidth;
    const h0 = el.clientHeight;
    if (w0 > 0) setContainerWidth(w0);
    if (h0 > 0) setContainerHeight(h0);
    return () => ro.disconnect();
  }, [resolvedFilePath]);

  const pageWidth = useMemo(() => Math.max(240, containerWidth - 16), [containerWidth]);
  const pageHeight = useMemo(
    () => Math.max(320, containerHeight - 16),
    [containerHeight],
  );
  const renderPageWidth = useMemo(
    () => Math.max(120, pageWidth * zoomScale),
    [pageWidth, zoomScale],
  );
  const renderPageHeight = useMemo(
    () => Math.max(180, pageHeight * zoomScale),
    [pageHeight, zoomScale],
  );
  const displayedPages = useMemo(() => {
    if (previewMode === "all") {
      return Array.from({ length: numPages }, (_, i) => i + 1);
    }
    return [pageNumber];
  }, [previewMode, numPages, pageNumber]);

  const beginPaintWait = useCallback((page: number) => {
    pendingPaintPageRef.current = page;
    setPagePaintPending(true);
  }, []);

  const onPageRenderSuccess = useCallback((page: number) => {
    if (pendingPaintPageRef.current != null && pendingPaintPageRef.current !== page) {
      return;
    }
    if (pendingPaintPageRef.current == null) {
      return;
    }
    pendingPaintPageRef.current = null;
    setPagePaintPending(false);
    setDocLoading(false);
  }, []);

  const writeMeta = useCallback(
    (page: number, total: number) => {
      if (!dataSetKey || !metaPrefix) return;
      setDataSet(dataSetKey, `${metaPrefix}.currentPage`, page);
      if (total > 0) {
        setDataSet(dataSetKey, `${metaPrefix}.totalPages`, total);
      }
    },
    [dataSetKey, metaPrefix, setDataSet],
  );

  useEffect(() => {
    if (numPages > 0) {
      writeMeta(pageNumber, numPages);
    }
  }, [pageNumber, numPages, writeMeta]);

  const goToPageInternal = useCallback(
    (target: number, behavior: ScrollBehavior = "auto") => {
      const next = numPages > 0
        ? Math.max(1, Math.min(Math.floor(target), numPages))
        : Math.max(1, Math.floor(target));
      if (previewMode === "single" && next !== pageNumberRef.current) {
        beginPaintWait(next);
      }
      setPageNumber(next);
      if (previewMode === "all") {
        const node = pageNodeMapRef.current[next];
        if (node) {
          node.scrollIntoView({ block: "start", behavior });
        }
      }
    },
    [numPages, previewMode, beginPaintWait],
  );

  const goToPageInternalRef = useRef(goToPageInternal);
  goToPageInternalRef.current = goToPageInternal;

  useEffect(() => {
    const tp = targetPageFromStore;
    if (typeof tp !== "number" || !Number.isFinite(tp)) return;
    goToPageInternal(tp, "auto");
    if (dataSetKey && metaPrefix) {
      setDataSet(dataSetKey, `${metaPrefix}.targetPage`, undefined);
    }
  }, [targetPageFromStore, goToPageInternal, dataSetKey, metaPrefix, setDataSet]);

  useEffect(() => {
    const initial = Math.max(1, propInitialPage ?? 1);
    setPageNumber(initial);
  }, [propInitialPage]);

  const onDocLoad = useCallback(
    ({ numPages: n }: { numPages: number }) => {
      setLoadError(null);
      const prevPage = pageNumberRef.current;
      const next = Math.min(Math.max(1, prevPage), n || 1);
      setNumPages(n);
      setPageNumber(next);
      // Document 就绪 ≠ 页已绘完，继续等目标页 onRenderSuccess
      beginPaintWait(next);
      scrollSyncSuppressedRef.current = true;
      pendingScrollPageRef.current = next;
    },
    [beginPaintWait],
  );

  const onDocLoadError = useCallback((e: Error) => {
    if (/worker is being destroyed/i.test(e.message) && resolvedFilePath) {
      setDisplayFile(undefined);
      setDocLoading(true);
      setPagePaintPending(false);
      pendingPaintPageRef.current = null;
      setLoadError(null);
      const gen = loadGenRef.current;
      const retry = () => {
        if (gen !== loadGenRef.current) return;
        setDisplayFile(resolvedFilePath);
      };
      if (typeof requestAnimationFrame !== "undefined") {
        requestAnimationFrame(() => requestAnimationFrame(retry));
      } else {
        queueMicrotask(retry);
      }
      return;
    }
    setLoadError(e);
    setDocLoading(false);
    setPagePaintPending(false);
    pendingPaintPageRef.current = null;
    scrollSyncSuppressedRef.current = false;
    pendingScrollPageRef.current = null;
  }, [resolvedFilePath]);

  useEffect(() => {
    const next = resolvedFilePath?.trim() || undefined;
    const gen = ++loadGenRef.current;

    if (!next) {
      setDisplayFile(undefined);
      setDocLoading(false);
      setPagePaintPending(false);
      pendingPaintPageRef.current = null;
      setLoadError(null);
      setZoomScale(1);
      setNumPages(0);
      scrollSyncSuppressedRef.current = false;
      pendingScrollPageRef.current = null;
      pageNodeMapRef.current = {};
      return;
    }

    setDocLoading(true);
    setPagePaintPending(false);
    pendingPaintPageRef.current = null;
    setLoadError(null);
    setZoomScale(1);
    setNumPages(0);
    scrollSyncSuppressedRef.current = true;
    pendingScrollPageRef.current = null;
    pageNodeMapRef.current = {};
    setDisplayFile(undefined);

    let cancelled = false;
    const commit = () => {
      if (cancelled || gen !== loadGenRef.current) return;
      setDisplayFile(next);
      mountSettledRef.current = true;
    };

    const waitFrames = mountSettledRef.current ? 2 : 3;
    let framesLeft = waitFrames;
    const tick = () => {
      if (cancelled) return;
      framesLeft -= 1;
      if (framesLeft <= 0) {
        commit();
        return;
      }
      requestAnimationFrame(tick);
    };

    if (typeof requestAnimationFrame !== "undefined") {
      requestAnimationFrame(tick);
    } else {
      queueMicrotask(commit);
    }

    return () => {
      cancelled = true;
    };
  }, [resolvedFilePath]);

  /**
   * 文档加载完成后，Page 组件可能还需要额外几帧才完成布局（clientHeight > 0）。
   * 用 rAF 重试直到目标页节点有实际高度，再 scrollIntoView + 解除滚动同步抑制。
   */
  useEffect(() => {
    const target = pendingScrollPageRef.current;
    if (target == null || loading || numPages <= 0 || previewMode !== "all") return;

    let cancelled = false;
    let frames = 0;
    const maxFrames = 120;

    const tryScroll = (): void => {
      if (cancelled) return;
      frames++;
      const clamped = Math.max(1, Math.min(Math.floor(target), numPages));

      let allReady = true;
      for (let i = 1; i <= clamped; i++) {
        const n = pageNodeMapRef.current[i];
        if (!n || !n.isConnected || n.clientHeight <= 50) {
          allReady = false;
          break;
        }
      }

      if (allReady) {
        requestAnimationFrame(() => {
          if (cancelled) return;
          const node = pageNodeMapRef.current[clamped];
          if (node) node.scrollIntoView({ block: "start", behavior: "auto" });
          pendingScrollPageRef.current = null;
          requestAnimationFrame(() => {
            if (!cancelled) scrollSyncSuppressedRef.current = false;
          });
        });
        return;
      }

      if (frames >= maxFrames) {
        pendingScrollPageRef.current = null;
        scrollSyncSuppressedRef.current = false;
        return;
      }
      requestAnimationFrame(tryScroll);
    };

    requestAnimationFrame(tryScroll);
    return () => { cancelled = true; };
  }, [loading, numPages, previewMode]);

  const adjustZoom = useCallback((delta: number) => {
    setZoomScale((prev) => {
      const next = Number((prev + delta).toFixed(2));
      return Math.max(MIN_ZOOM_SCALE, Math.min(MAX_ZOOM_SCALE, next));
    });
  }, []);

  const runPrint = useCallback(() => {
    if (!resolvedFilePath) return;
    const w = window.open(resolvedFilePath, "_blank", "noopener,noreferrer");
    if (w) {
      w.onload = () => {
        try {
          w.print();
        } catch {
          /* ignore */
        }
      };
    }
  }, [resolvedFilePath]);

  const runDownload = useCallback(() => {
    if (!resolvedFilePath) return;
    const a = document.createElement("a");
    a.href = resolvedFilePath;
    a.download = "";
    a.rel = "noopener noreferrer";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [resolvedFilePath]);

  const runEnterFullscreen = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    void el.requestFullscreen?.();
  }, []);

  const runExitFullscreen = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen();
  }, []);

  const handleViewerScroll = useCallback(() => {
    if (docLoading || previewMode !== "all" || numPages <= 0) return;
    if (scrollSyncSuppressedRef.current) return;
    const container = viewerBodyRef.current;
    if (!container) return;
    const anchorY = container.scrollTop + container.clientHeight * 0.35;
    let bestPage = pageNumber;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let i = 1; i <= numPages; i++) {
      const node = pageNodeMapRef.current[i];
      if (!node) continue;
      const dist = Math.abs(node.offsetTop - anchorY);
      if (dist < bestDist) {
        bestDist = dist;
        bestPage = i;
      }
    }
    if (bestPage !== pageNumber) {
      setPageNumber(bestPage);
    }
  }, [docLoading, previewMode, numPages, pageNumber]);

  /** single 模式仅渲染一页，容器不可滚动；用滚轮离散翻页 */
  const handleViewerWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (loading || previewMode !== "single" || numPages <= 1) return;
      wheelAccumRef.current += e.deltaY;
      if (Math.abs(wheelAccumRef.current) < 80 || wheelFlipLockRef.current) return;

      const direction = wheelAccumRef.current > 0 ? 1 : -1;
      wheelAccumRef.current = 0;
      const next = pageNumber + direction;
      if (next < 1 || next > numPages) return;

      e.preventDefault();
      wheelFlipLockRef.current = true;
      goToPageInternal(next);
      window.setTimeout(() => {
        wheelFlipLockRef.current = false;
      }, 280);
    },
    [loading, previewMode, numPages, pageNumber, goToPageInternal],
  );

  useEffect(() => {
    if (!storeProps || typeof storeProps !== "object") return;
    const nonce = (storeProps as any)._pdfCmdNonce as number | undefined;
    const cmd = (storeProps as any).pdfCommand as
      | PdfViewerCommand
      | "fitWidth"
      | "fitHeight"
      | "previewSingle"
      | "previewAll"
      | undefined;
    const arg = (storeProps as any).pdfCommandArg as number | undefined;
    if (nonce == null || nonce === lastCmdNonce.current || !cmd) return;
    lastCmdNonce.current = nonce;

    switch (cmd) {
      case "print":
        runPrint();
        break;
      case "download":
        runDownload();
        break;
      case "enterFullscreen":
        runEnterFullscreen();
        break;
      case "exitFullscreen":
        runExitFullscreen();
        break;
      case "goToPage":
        if (typeof arg === "number") goToPageInternal(arg, "smooth");
        break;
      case "fitWidth":
        setFitMode("width");
        setZoomScale(1);
        break;
      case "fitHeight":
        setFitMode("height");
        setZoomScale(1);
        break;
      case "zoomIn":
        adjustZoom(ZOOM_STEP);
        break;
      case "zoomOut":
        adjustZoom(-ZOOM_STEP);
        break;
      case "previewSingle":
        setPreviewMode("single");
        break;
      case "previewAll":
        setPreviewMode("all");
        break;
      default:
        break;
    }

    if (sessionId && id) {
      const latest = useAndStore.getState().getProps(sessionId, id);
      const base =
        latest && typeof latest === "object" && !Array.isArray(latest) ? latest : {};
      useAndStore.getState().setProps(sessionId, id, {
        ...base,
        pdfCommand: undefined,
        pdfCommandArg: undefined,
      });
    }
  }, [
    storeProps,
    sessionId,
    id,
    runPrint,
    runDownload,
    runEnterFullscreen,
    runExitFullscreen,
    goToPageInternal,
    adjustZoom,
  ]);

  void _styles;
  void _token;

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flexGrow: 1,
        ...style,
      }}
    >
      <div
        ref={viewerBodyRef}
        onScroll={handleViewerScroll}
        onWheel={handleViewerWheel}
        style={{
          flex: 1,
          width: "100%",
          overflow: "auto",
          position: "relative",
          backgroundColor: resolvedNonPdfBackgroundColor,
          border: develop ? "1px dashed #ccc" : undefined,
        }}
      >
        {loadError ? (
          <Alert
            type="error"
            message={loadErrorMessage?.trim() || loadError.message}
            showIcon
          />
        ) : !resolvedFilePath && previewPending ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: resolvedNonPdfBackgroundColor,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                whiteSpace: "nowrap",
              }}
            >
              <Spin />
              <span style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.65)" }}>
                PDF 加载中…
              </span>
            </div>
          </div>
        ) : !resolvedFilePath ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <Empty
              image={
                <FilePdfOutlined
                  style={{
                    fontSize: 72,
                    color: "rgba(0, 0, 0, 0.25)",
                  }}
                />
              }
              description={
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      marginBottom: 8,
                      fontSize: 14,
                      color: "rgba(0, 0, 0, 0.88)",
                    }}
                  >
                    暂无预览
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
                    请传入文件路径
                  </div>
                </div>
              }
            />
          </div>
        ) : (
          <>
            {loading || !displayFile ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // 换文件：盖住旧画面；换页：半透明保留上下文
                  backgroundColor: docLoading || !displayFile
                    ? resolvedNonPdfBackgroundColor
                    : "rgba(236, 236, 236, 0.72)",
                  zIndex: 2,
                  pointerEvents: "none",
                  transition: "background-color 120ms ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 10,
                    whiteSpace: "nowrap",
                  }}
                >
                  <Spin />
                  <span style={{ fontSize: 14, color: "rgba(0, 0, 0, 0.65)" }}>
                    {docLoading || !displayFile ? "加载中…" : "渲染中…"}
                  </span>
                </div>
              </div>
            ) : null}
            {displayFile ? (
            <Document
              file={displayFile}
              onLoadSuccess={onDocLoad}
              onLoadError={onDocLoadError}
              loading={null}
              options={docOptions}
            >
              {displayedPages.map((p) => (
                <div
                  key={p}
                  ref={(node) => {
                    pageNodeMapRef.current[p] = node;
                  }}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Page
                    pageNumber={p}
                    width={fitMode === "width" ? renderPageWidth : undefined}
                    height={fitMode === "height" ? renderPageHeight : undefined}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    onRenderSuccess={() => onPageRenderSuccess(p)}
                  />
                </div>
              ))}
            </Document>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default AndPdfViewer;
